/**
 * O que faz: Ações prioritárias calculadas sobre agregados válidos (responder, falhas, retornos).
 * Onde usa: Dashboard, no topo da seção "Resultados por canal" (abaixo dos gráficos).
 * Conexões: Recebe contagens por props; navegação via callbacks com filtro exato.
 * Tabelas: Nenhuma direta; números vêm de atendimento, disparos e operação.
 * Data: 05/09/2026 (faixa compacta em pílulas em 05/10/2026).
 */

import { ArrowRight, Zap } from 'lucide-react';

interface AcaoPrioritaria {
  id: string;
  titulo: string;
  quantidade: number;
  filtro: FiltroAcaoPrioritaria;
}

export type FiltroAcaoPrioritaria = 'sem-resposta' | 'falhas' | 'retornos-vencidos';

interface AcoesPrioritariasDashboardProps {
  semResposta: number;
  falhasEnvio: number;
  retornosVencidos: number;
  onAbrirFila: (filtro: FiltroAcaoPrioritaria) => void;
}

export function montarAcoesPrioritarias(semResposta: number, falhasEnvio: number, retornosVencidos: number): AcaoPrioritaria[] {
  // Zero não gera alerta; fonte indisponível (negativo) nunca vira recomendação.
  const acoes: AcaoPrioritaria[] = [];
  if (semResposta > 0) acoes.push({ id: 'responder', titulo: `Responder ${semResposta} conversas`, quantidade: semResposta, filtro: 'sem-resposta' });
  if (falhasEnvio > 0) acoes.push({ id: 'falhas', titulo: `Revisar ${falhasEnvio} falhas de envio`, quantidade: falhasEnvio, filtro: 'falhas' });
  if (retornosVencidos > 0) acoes.push({ id: 'retornos', titulo: `Acompanhar ${retornosVencidos} retornos vencidos`, quantidade: retornosVencidos, filtro: 'retornos-vencidos' });
  return acoes.slice(0, 3);
}

export function AcoesPrioritariasDashboard({ semResposta, falhasEnvio, retornosVencidos, onAbrirFila }: AcoesPrioritariasDashboardProps) {
  const acoes = montarAcoesPrioritarias(semResposta, falhasEnvio, retornosVencidos);
  if (acoes.length === 0) return null;
  // Faixa de uma linha em pílulas: antes era um card cheio com um botão por linha.
  return (
    <section aria-label="Ações prioritárias" className="flex flex-wrap items-center gap-2">
      <h3 className="mr-1 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        <Zap className="h-3.5 w-3.5" aria-hidden="true" />Ações prioritárias
      </h3>
      <ul className="flex flex-wrap gap-2">
        {acoes.map((acao) => (
          <li key={acao.id}>
            <button
              type="button"
              onClick={() => onAbrirFila(acao.filtro)}
              className="group inline-flex min-h-9 items-center gap-2 rounded-full bg-[#13291b] py-1.5 pl-3.5 pr-2.5 text-[13px] font-medium text-[#f6f9f1] transition-colors hover:bg-[#30412a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none dark:bg-accent dark:text-accent-foreground"
            >
              <span>{acao.titulo}</span>
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-150 group-hover:translate-x-0.5 motion-reduce:transition-none" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
